import type { TextRun } from '@/api/types/stowed-element'
import { familyCss, textDecorationCss, variantStyle, variantWeight } from '../helpers/run-font-css'
import type { ShapeTextCapBox, ShapeTextLayoutState } from '../types/shape-text-layout-state'

/**
 * The fitted drop-cap glyph itself, painted independently of the small hidden span in
 * `ShapeTextChunk` — see the `dropCapCharIndices` comment in `shape-text-editor.tsx` for why the
 * character has two spans. Sized to its own solved `fontSizePt`
 * (`solveDropCapFontSizePt`/`layoutShapeTextWithDropCap`) rather than the paragraph's run size.
 * `lineHeight: 'normal'` is load-bearing, not cosmetic: `box.yTop` was solved in
 * `shape-drop-cap.ts` against the CSS `line-height: normal` convention this codebase already relies
 * on elsewhere (`canvas-font-metrics.ts`'s `measureFontAscentDescentPt` doc comment) — baseline sits
 * `fontBoxAscentPt` below `top`. An explicit pixel `lineHeight` (as this used to set, equal to the
 * cap's own target height) uses a DIFFERENT browser algorithm to place the baseline, so it would
 * silently stop matching the position `shape-drop-cap.ts` solved for.
 */
export function DropCapGlyph({
  box,
  run,
  fonts,
  familyId,
  cssFamily,
  fontWeight,
  fontStyle,
}: Pick<ShapeTextLayoutState, 'fonts' | 'familyId' | 'cssFamily' | 'fontWeight' | 'fontStyle'> & {
  box: ShapeTextCapBox
  run: TextRun | null
}) {
  return (
    <div
      className="absolute select-none whitespace-pre pointer-events-none"
      style={{
        left: box.x,
        top: box.yTop,
        lineHeight: 'normal',
        fontFamily: familyCss(box.fontFamilyId ?? familyId, fonts, cssFamily),
        fontSize: `${box.fontSizePt}px`,
        fontWeight: variantWeight(box.fontId, fonts, fontWeight),
        fontStyle: variantStyle(box.fontId, fonts, fontStyle),
        textDecoration: textDecorationCss(run),
        fontKerning: 'none',
        fontVariantLigatures: 'none',
      }}
    >
      {box.character}
    </div>
  )
}
